// Barre de navigation latérale avec sous-menus repliables.
import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { NAVIGATION } from "@/lib/navigation";
import { roleEffectif } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { ChevronDown } from "lucide-react";

export function Sidebar() {
  const location = useLocation();
  const { user, abonnement } = useAuth();
  const role = roleEffectif(user);
  // Filtrage par rôle ET par module optionnel (item sans contrainte = visible).
  const items = NAVIGATION.filter((item) => {
    if (item.roles && !item.roles.includes(role)) return false;
    if (item.module === "immobilisations" && !abonnement?.module_immobilisations) return false;
    return true;
  });
  // Sous-menu ouvert par défaut si l'on s'y trouve
  const ouvertInitial = items.find(
    (item) => item.sousMenu && item.sousMenu.some((s) => location.pathname.startsWith(s.chemin))
  )?.cle;
  const [ouvert, setOuvert] = useState(ouvertInitial || null);

  const basculer = (cle) => setOuvert((o) => (o === cle ? null : cle));

  return (
    <aside
      data-testid="barre-navigation"
      className="no-print w-64 flex-shrink-0 bg-[#1D3D3D] text-white flex flex-col h-screen sticky top-0"
    >
      <div className="h-16 flex items-center px-6 border-b border-white/10">
        <span data-testid="logo-sidebar" className="text-lg font-black tracking-tight">
          Porinetia Compta <span className="text-[#4A8A8A]">AERH</span>
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((item) => {
          const Icone = item.icone;

          // Élément simple (sans sous-menu)
          if (!item.sousMenu) {
            return (
              <NavLink
                key={item.cle}
                to={item.chemin}
                end={item.chemin === "/tableau-de-bord"}
                data-testid={`nav-${item.cle}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#2D5A5A] text-white border-l-2 border-[#4A8A8A]"
                      : "text-white/75 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icone className="h-[18px] w-[18px]" />
                {item.libelle}
              </NavLink>
            );
          }

          // Élément avec sous-menu repliable
          const sousActif = item.sousMenu.some((s) => location.pathname.startsWith(s.chemin));
          // Le sous-menu reste ouvert si l'on se trouve sur l'une de ses sous-routes.
          const estOuvert = ouvert === item.cle || sousActif;
          return (
            <div key={item.cle}>
              <button
                type="button"
                data-testid={`nav-${item.cle}`}
                onClick={() => basculer(item.cle)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  sousActif ? "text-white" : "text-white/75 hover:bg-white/5 hover:text-white"
                }`}
              >
                <span className="flex items-center gap-3">
                  <Icone className="h-[18px] w-[18px]" />
                  {item.libelle}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${estOuvert ? "rotate-180" : ""}`} />
              </button>
              {estOuvert && (
                <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-1">
                  {item.sousMenu.map((sous) => (
                    <NavLink
                      key={sous.chemin}
                      to={sous.chemin}
                      data-testid={`nav-${sous.chemin.replace(/\//g, "-").replace(/^-/, "")}`}
                      className={({ isActive }) =>
                        `block px-3 py-2 rounded-md text-sm transition-colors ${
                          isActive
                            ? "bg-[#2D5A5A] text-white"
                            : "text-white/65 hover:bg-white/5 hover:text-white"
                        }`
                      }
                    >
                      {sous.libelle}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

export default Sidebar;
